import { useEffect, useRef, useState } from "react";
import {
    Box, Button, Checkbox, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogContentText, FormControlLabel,
    Grid, IconButton, Snackbar, TextField, Typography
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import { getApiUrl } from "../services/api_service";
import { Alert, Autocomplete } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { IMaskInput } from 'react-imask';
import { mainGrid, orangecolor, pcolor, rcolor, whitecolor } from "../style2";

export default function EditReferral({ open, onClose, onSaved, orgCode, referralId, disabled = false }) {

    // ── Form field state ──────────────────────────────────────────────────────
    const [firstName, setFirstName] = useState('');
    const [surName, setSurName] = useState('');
    const [nhsNumber, setNHSNumber] = useState('');
    // DOB is loaded for display only — NOT editable
    const [dob, setDob] = useState('');
    const [address, setAddress] = useState('');
    const [postCode, setPostCode] = useState('');
    const [parentForename, setParentForename] = useState('');
    const [parentSurname, setParentSurname] = useState('');
    const [parentEmail, setParentEmail] = useState('');
    const [proposedSubmissionDate, setProposedSubmissionDate] = useState(null);
    const [reminder, setReminder] = useState(null);

    // ── UI / async state ───────────────────────────────────────────────────────
    const [loadingData, setLoadingData] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');


    // Postcode-based address suggestions (same as MakeReferral)
    const [addresses, setAddresses] = useState([]);

    // NHS number confirmation modal state
    const [showNHSConfirmationModal, setShowNHSConfirmationModel] = useState(false);
    const [nhsChanged, setNHSChanged] = useState(false);
    const nhsRef = useRef(null);

    // ── Validation state ──────────────────────────────────────────────────────
    const [formSubmitted, setFormSubmitted] = useState(false);
    const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isNHSNumberValid = (nhs) => /^\d{3}-\d{3}-\d{4}$/.test(nhs);
    const fieldBorderColor = (isValid) => formSubmitted && !isValid ? 'red' : pcolor;

    // ── Load referral data by ID on mount ─────────────────────────────────────
    useEffect(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            onClose();
            return;
        }

        setLoadingData(true);
        setError('');
        setFormSubmitted(false);

        fetch(`${getApiUrl()}/referralapp/patient/${referralId}?code=${orgCode}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            .then(response => {
                if (response.status === 200) {
                    response.json().then(data => {
                        // Populate form fields from the loaded referral
                        setFirstName(data.firstName || '');
                        setSurName(data.surName || '');
                        setNHSNumber(data.nhsNumber || '');
                        setDob(data.dob || '');
                        setAddress(data.address || '');
                        setPostCode(data.postCode || '');
                        const nameParts = (data.parentName || '').split(' ');
                        setParentForename(nameParts[0] || '');
                        setParentSurname(nameParts.slice(1).join(' ') || '');
                        setParentEmail(data.parentEmail || '');
                        setProposedSubmissionDate(data.proposedSubmissionDate || null);
                        setReminder(data.reminder || null);
                    });
                } else if (response.status === 401 || response.status === 403) {
                    localStorage.clear();
                    onClose();
                } else {
                    response.json().then(data => {
                        setError(data.error || "Unable to load referral.");
                    }).catch(() => {
                        setError("Unable to load referral.");
                    });
                }
            })
            .catch(() => {
                setError("Unable to contact server.");
            })
            .finally(() => {
                setLoadingData(false);
            });
    }, [referralId, orgCode]);

    // ── Fetch address suggestions whenever postcode changes ───────────────────
    useEffect(() => {
        if (!postCode || postCode.length < 3) return;

        fetch(`${getApiUrl()}/referralapp/addresses?code=${postCode}`, {
            method: 'GET'
        })
            .then(response => {
                if (response.status === 200) {
                    response.json().then(data => {
                        if (data?.results) {
                            const suggestions = data.results.map(item => {
                                let a = `${item.line_1} ${item.line_2} ${item.line_3} ${item.line_4} ${item.locality} ${item.town_or_city} ${item.county}`;
                                return a.replace(/\s\s+/g, ' ');
                            });
                            setAddresses(suggestions);
                        } else {
                            setAddresses([]);
                        }
                    }).catch(() => setAddresses([]));
                } else {
                    setAddresses([]);
                }
            })
            .catch(() => setAddresses([]));
    }, [postCode]);

    // ── Save updated referral data ─────────────────────────────────────────────
    const saveData = () => {
        setFormSubmitted(true);
        if (!isNHSNumberValid(nhsNumber)) {
            setError('NHS Number is invalid. Please check and re-enter a valid 10-digit NHS Number.');
            return;
        }
        setError('');
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            onClose();
            return;
        }

        setError('');
        setSuccess('');
        setSaving(true);

        // Build the EditPatientReferralModel payload — DOB is intentionally excluded
        const payload = {
            firstName,
            surName,
            nhsNumber,
            address,
            postCode,
            parentName: `${parentForename} ${parentSurname}`.trim(),
            parentEmail,
            proposedSubmissionDate: proposedSubmissionDate || null,
            reminder: reminder || null
        };

        fetch(`${getApiUrl()}/referralapp/editreferral/${referralId}/${orgCode}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            .then(response => {
                if (response.status === 200) {
                    setSuccess('Referral updated successfully.');
                    onSaved();
                } else {
                    response.json().then(data => {
                        setError(data.error || "Unable to save changes.");
                    }).catch(() => {
                        setError("Unable to process request.");
                    });
                }
            })
            .catch(() => {
                setError("Unable to contact server.");
            })
            .finally(() => {
                setSaving(false);
            });
    };



    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "1.3rem", color: pcolor }}>{disabled ? "View" : "Edit"} Referral</Typography>
                <IconButton onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3 }}>

                {/* Loading spinner while fetching existing referral data */}
                {loadingData ? (
                    <Box sx={{ textAlign: "center", py: 5 }}>
                        <CircularProgress color="inherit" size={30} />
                        <Typography sx={{ mt: 1, color: pcolor }}>Loading referral...</Typography>
                    </Box>
                ) : (
                    <Box sx={{ ...mainGrid, background: whitecolor, padding: "1rem" }}>
                        <form onSubmit={(e) => { e.preventDefault(); saveData(); }}>
                            <Grid container sx={{ flexGrow: 1 }} spacing={0}>

                                {/* ── Left column: Child's profile ─────────────────── */}
                                <Grid size={6} item>
                                    <Box pl={3} pr={6}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold", color: rcolor, marginBottom: "2rem" }}>
                                            {disabled ? "View" : "Edit"} Child's Profile
                                        </Typography>

                                        {/* Forename */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Forename*</label>
                                            <TextField
                                                inputProps={{ maxLength: 50 }}
                                                sx={{ border: `1px solid ${fieldBorderColor(firstName.trim().length > 0)}`, borderRadius: 2, width: "80%" }}
                                                required variant="outlined" fullWidth
                                                value={firstName} disabled={disabled}
                                                onChange={(e) => setFirstName(e.target.value)}
                                            />
                                        </Box>

                                        {/* Surname */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Surname*</label>
                                            <TextField
                                                inputProps={{ maxLength: 50 }}
                                                sx={{ border: `1px solid ${fieldBorderColor(surName.trim().length > 0)}`, borderRadius: 2, width: "80%" }}
                                                required variant="outlined" fullWidth
                                                value={surName} disabled={disabled}
                                                onChange={(e) => setSurName(e.target.value)}
                                            />
                                        </Box>

                                        {/* NHS Number — shows confirmation dialog on change */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>NHS Number*</label>
                                            <IMaskInput
                                                mask="000-000-0000"
                                                value={nhsNumber}
                                                inputRef={nhsRef} disabled={disabled}
                                                onAccept={(value) => {
                                                    if (value !== nhsNumber) setNHSChanged(true);
                                                    else setNHSChanged(false);
                                                    setNHSNumber(value);
                                                }}
                                                onBlur={() => {
                                                    setTimeout(() => {
                                                        if (nhsNumber.length === 12 && nhsChanged) {
                                                            setShowNHSConfirmationModel(true);
                                                            setNHSChanged(false);
                                                        }
                                                    }, 400);
                                                }}
                                                style={{ border: `1px solid ${fieldBorderColor(isNHSNumberValid(nhsNumber))}`, fontSize: "16px", borderRadius: 5, width: "80%", padding: "16.5px 14px" }}
                                                overwrite
                                                required
                                                render={(ref, props) => (
                                                    <TextField {...props} inputRef={ref} variant="outlined" fullWidth required />
                                                )}
                                            />

                                            {/* NHS number change confirmation dialog */}
                                            <Dialog
                                                open={showNHSConfirmationModal}
                                                onClose={(_, reason) => { if (reason !== "backdropClick") setShowNHSConfirmationModel(false); }}
                                                disableRestoreFocus
                                            >
                                                <DialogContent>
                                                    <DialogContentText>
                                                        <Box style={{ textAlign: "center" }}>
                                                            <ReportGmailerrorredIcon style={{ fontSize: "2.6rem" }} />
                                                        </Box>
                                                        <Typography style={{ fontSize: "1.6rem", marginBottom: "10px", textAlign: "center" }}>Confirmation!</Typography>
                                                        <Typography style={{ fontSize: "1.2rem", textAlign: "center" }}>
                                                            Is the NHS Number correct?<br />{nhsNumber}
                                                        </Typography>
                                                    </DialogContentText>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button onClick={() => {
                                                        setShowNHSConfirmationModel(false);
                                                        setTimeout(() => { nhsRef.current?.focus(); }, 400);
                                                    }}>No</Button>
                                                    <Button onClick={() => setShowNHSConfirmationModel(false)} autoFocus>Yes</Button>
                                                </DialogActions>
                                            </Dialog>
                                        </Box>

                                        {/* Date of Birth — read-only, shown as plain text (not editable) */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>D.O.B (not editable)</label>
                                            <Typography sx={{
                                                border: `1px solid ${pcolor}`, borderRadius: 2, width: "80%",
                                                padding: "16.5px 14px", fontSize: "1rem", color: pcolor,
                                                backgroundColor: "rgba(17, 92, 113, 0.04)"
                                            }}>
                                                {dob && dayjs(dob).isValid() ? dayjs(dob).format("DD/MM/YYYY") : "—"}
                                            </Typography>
                                        </Box>

                                        {/* Postcode — triggers address lookup */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Postcode*</label>
                                            <TextField
                                                slotProps={{ input: { maxLength: 10 } }}
                                                sx={{ border: `1px solid ${fieldBorderColor(postCode.trim().length > 0)}`, borderRadius: 2, width: "80%" }}
                                                required variant="outlined" fullWidth
                                                value={postCode} disabled={disabled}
                                                onChange={(e) => setPostCode(e.target.value)}
                                            />
                                        </Box>

                                        {/* Home address with postcode autocomplete */}
                                        <Box mb={4}>
                                            <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Home Address*</label>
                                            <Autocomplete
                                                options={addresses}
                                                value={address}
                                                freeSolo disabled={disabled}
                                                inputValue={address}
                                                onInputChange={(_, newInputValue) => setAddress(newInputValue)}
                                                onChange={(_, newValue) => setAddress(newValue || '')}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputProps={{ ...params.inputProps, maxLength: 200 }}
                                                        variant="outlined" required multiline
                                                        style={{ border: `1px solid ${fieldBorderColor(address.trim().length > 0)}`, borderRadius: 5, width: "80%" }}
                                                        disabled={disabled}
                                                    />
                                                )}
                                            />
                                            <Typography style={{ fontSize: "0.9rem", marginTop: "10px" }}>Start typing postcode and select address from dropdown or enter manually.</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* ── Right column: Parent / Carer details ─────────── */}
                                <Grid size={6} item>
                                    <Typography variant="h5" style={{ fontWeight: "bold", color: rcolor, marginBottom: "2rem" }}>
                                        {disabled ? "View" : "Edit"} Parent or Carer
                                    </Typography>

                                    {/* Parent forename */}
                                    <Box mb={4}>
                                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Parent Forename*</label>
                                        <TextField
                                            inputProps={{ maxLength: 50 }}
                                            style={{ border: `1px solid ${fieldBorderColor(parentForename.trim().length > 0)}`, borderRadius: 5, width: "80%" }}
                                            required variant="outlined" fullWidth
                                            value={parentForename}
                                            onChange={(e) => setParentForename(e.target.value)}
                                            disabled={disabled}
                                        />
                                    </Box>

                                    {/* Parent surname */}
                                    <Box mb={4}>
                                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Parent Surname*</label>
                                        <TextField
                                            inputProps={{ maxLength: 50 }}
                                            style={{ border: `1px solid ${fieldBorderColor(parentSurname.trim().length > 0)}`, borderRadius: 5, width: "80%" }}
                                            required variant="outlined" fullWidth
                                            value={parentSurname} disabled={disabled}
                                            onChange={(e) => setParentSurname(e.target.value)}
                                        />
                                    </Box>

                                    {/* Parent email */}
                                    <Box mb={4}>
                                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Parent Email*</label>
                                        <TextField
                                            inputProps={{ maxLength: 100, type: "email" }}
                                            style={{ border: `1px solid ${fieldBorderColor(parentEmail.trim().length > 0 && isEmailValid(parentEmail))}`, borderRadius: 5, width: "80%" }}
                                            required variant="outlined" fullWidth
                                            value={parentEmail} disabled={disabled}
                                            onChange={(e) => setParentEmail(e.target.value)}
                                        />
                                    </Box>

                                    {/* Proposed submission date — optional */}
                                    <Box mb={4}>
                                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>
                                            Proposed Submission Date (optional)
                                        </label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                format="DD/MM/YYYY" disabled={disabled}
                                                value={proposedSubmissionDate && dayjs(proposedSubmissionDate).isValid() ? dayjs(proposedSubmissionDate) : null}
                                                onChange={(newValue) => {
                                                    setProposedSubmissionDate(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : null);
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'outlined',
                                                        style: { border: `1px solid ${pcolor}`, borderRadius: 5, width: '80%' }
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>

                                    {/* Reminder — weekly email cadence */}
                                    <Box mb={4}>
                                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Reminder</label>
                                        <FormControlLabel
                                            control={
                                                <Checkbox disabled={disabled}
                                                    checked={reminder === 1}
                                                    onChange={(e) => setReminder(e.target.checked ? 1 : null)}
                                                />
                                            }
                                            label="Weekly"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Error / success feedback */}
                            {error !== '' ? <Box mb={1} mt={1}><Alert severity="error">{error}</Alert></Box> : null}
                            <Snackbar
                                open={success !== ''}
                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                autoHideDuration={6000}
                                onClose={() => setSuccess('')}
                                message={success}
                                action={
                                    <IconButton size="small" color="inherit" onClick={() => setSuccess('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                }
                            />
                            {!disabled  ? <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "row", padding: 3 }}>
                                <Button
                                    type="button"
                                    disabled={saving}
                                    onClick={onClose}
                                    style={{
                                        fontSize: "1.1rem", height: "40px", width: "150px", marginRight: "30px",
                                        borderRadius: 15, background: whitecolor, border: `2px solid ${orangecolor}`,
                                        textTransform: "capitalize"
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    disabled={saving}
                                    onClick={() => setFormSubmitted(true)}
                                    style={{
                                        background: orangecolor, fontSize: "1.1rem", height: "40px",
                                        width: "150px", borderRadius: 15, color: whitecolor, textTransform: "capitalize"
                                    }}
                                >
                                    {saving ? <CircularProgress color="inherit" size="20px" style={{ marginRight: 8 }} /> : "Save Changes"}
                                    
                                </Button>
                            </Box> : null}
                            {/* Form action buttons */}
                            
                        </form>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
